import React, { Component } from 'react';
import Text from './Text';
import CM from '../images/cm.png';
import RNZ from '../images/returns_no_zoom.png';
import RZ from '../images/returns_zoom.png';
import RN from '../images/returns_N.png';
import RP from '../images/returns_P.png';
import RV from '../images/returns_V.png';
import GD from '../images/gradient_descent.png';
import RMSP from '../images/rmsprop.png';
import MO from '../images/multi_opti.png';
import MOZ from '../images/multi_opti_zoom.png';
import { NavLink } from 'react-router-dom';

const publicPath = "/";

const TransDescription = <div id="Descript">

Welcome to part 5!<br/><br/>

Previously in this tutorial we were trying to forecast the price for the next day to then use that prediction to take financial decisions.
There are two problems with this method. First, the data is heterogeneous,
at the beginning the prices are low and the volatility is high and then prices are higher and volatility is slightly lower than before. Second, we care about returns;
we want to know if tomorrow’s price will be higher or lower, not the exact value (unless we are trading options).
That’s why we will transform the data from prices to returns.<br/><br/>

You may ask “Why didn’t you do that from the beginning?” Well, the point of this tutorial is not only to learn how-to but also <i>why</i> we do some transformations or use certain parameters.
If we don’t understand <i>why</i>, we won’t be able to achieve better results.<br/><br/>

In this part and the next one I will only explain the sections of code that are significantly different from previous parts of the tutorial.
The full code used for every step and the final models can be found on my <a href="https://github.com/JRachadell/Bitcoin_Deep_Learning">github</a>.<br/><br/>

To do the transformation let's use these lines of code at the beginning, right after removing the first 800 rows, that now will be changed to 100.<br/><br/>

<code>
df = df[100:]<br/>
df['Returns'] = df['Price'].pct_change()<br/>
df = df[1:]
</code>
<br/><br/>

The other thing I'll change is the number of observations used for predicting, they were 20 but now that we have changed to returns I consider a lower number is better.<br/><br/>

<code>
Obs = 12
</code>
<br/><br/>

At the beginning we used mean absolute percentage error as the loss function because we cared about the difference in percentage between the prices and the predictions.
Now that the data is transformed to returns we don’t need  mean absolute percentage error, we will use mean squared error.<br/><br/>

Back in Part 3 I said that I would explain how to choose the optimizer in Part 5 so here it goes,
it gets pretty technical so feel free to skip this if you don't want to get confused,
but you should understand it if you are planning to implement any of this in real life.<br/><br/>

The optimization of the loss function is done using Stochastic Gradient Descent,
the model will move in steps and in certain directions trying to find the weights that will minimize the loss function.
In the next chart imagine the red zones are the ones with the worst weights (the model's predictions are pretty bad)
and the blue zones are the ones with the best weights (the model's predictions are pretty good).
The optimizer is the set of rules (in what direction and how far to move) that the model will follow to try to find the blue zones.<br/><br/>

<img src={GD} alt="GD"/>
<br/>

Some optimizers will find the best blue zone (called global optima) and some of them won't, they will find a blue zone but not the best (this is called local optima,
for example the black path that goes to the right side of the chart).
Some optimizers will find the best blue zone after a very long time (hours), and some will find it very fast (minutes).
To learn more about optimizers check this great post: <a href="http://ruder.io/optimizing-gradient-descent/index.html">An overview of gradient descent optimization algorithms</a>.
<br/><br/>

So, what parameter should we choose to find the best blue zone (global optima) as fast as possible?
That depends mostly on the data, some optimizers will work better for some types of data, for example, image recognition, time series, playing games, etc...
Lucky for you, I tested all the <a href="https://keras.io/optimizers/">optimizers</a> with 1200 epochs, a batch size of 50, an output_dim of 20 and Keras's default arguments.
For each optimizer I saved the loss (errors) at each epoch, that is the accuracy of the model after modifying the weights everytime it analyzes the data once.
The lower the loss, the better.<br/><br/>

Let's check the loss of a single algorithm first, I'll start with the one we used before: "rmsprop".<br/><br/>

<img src={RMSP} alt="RMSP"/>
<br/>

On the Y axis we have the loss of the model, that's what we want to minimize; on the X axis we have the number of epochs.
As we can see, the more epochs, the accuracy get's better (lower loss) but also the model takes more time to train,
with my current settings each epoch takes around 1 second, that is 20 minutes for 1200 epochs (and I'm using GPU!).
We want the optimizer that will find the lower loss using the lower number of epochs.
Now I'll plot the same chart but with the losses per epoch of all the optimizers.<br/><br/>

<img src={MO} alt="MO"/>
<br/>

Looking at the chart, "nadam" (pink) seems like the faster optimizer, let's zoom in the last part to check which one found the lowest loss.<br/><br/>

<img src={MOZ} alt="MOZ"/>
<br/>

Well it's unanimous, "nadam" (pink) not only was faster than the rest, it also found the best values! "adam" (brown) was the second and "rmsprop" (orange) was third.<br/><br/>

Now we have all the parameters ready:<br/>
- Obs = 12<br/>
- loss = "mse"<br/>
- optimizer = "nadam"<br/>
- output_dim = 20<br/>
- epochs = 1200 (Can be way lower without sacrificing accuracy)<br/>
- batch_size = 50 (as before)
<br/><br/>

Batch_size, output_dim and other parameters like the architecture of the neural network can be optimized further but that would take way more time.
Let's proceed to train the model and wait for it to be ready.<br/><br/>

The metrics to test the predictions also change. We will do the following for both returns and predictions:
if the values are higher than 3%, we will tag them as “Higher”,
if the values are between 3% and -3% we will tag them as “Neutral” and if the values are lower than -3% we will tag them as “Lower”.<br/><br/>

Now we will proceed to visualize the metrics, with this for loop we will see how many times the predicted performace (Higher, Neutral, Lower) is the same as the real performance.<br/><br/>

<code>
rets = []<br/>
pred_rets = []<br/>
equals = []<br/>
Returns = list(dff['Returns'])<br/>
Prediction = list(dff['Predictions'])<br/>
for i in range(len(dff)):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;if Returns[i] > 0.03:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;rets.append("Higher")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;elif 0.03 >= Returns[i] >= -0.03:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;rets.append("Neutral")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;elif -0.03 > Returns[i]:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;rets.append("Lower")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;if Prediction[i] > 0.03:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;pred_rets.append("Higher")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;elif 0.03 >= Prediction[i] >= -0.03:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;pred_rets.append("Neutral")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;elif -0.03 > Prediction[i]:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;pred_rets.append("Lower")<br/>
&nbsp;<br/>
for i in range(len(dff)):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;if rets[i] == pred_rets[i]:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;equals.append(1)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;else:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;equals.append(0)<br/>
&nbsp;<br/>
print "% Total Correct:", np.average(equals) * 100
</code>

<br/><br/>

My output is the following:<br/><br/>
<mark>
% of Total Correct: 88.8
</mark>
<br/><br/>

The value is pretty high, that’s good. The next thing we will visualize is the confusion matrix, it is important to know that some errors are more important than others.
For example if the model predicts “Higher” and the returns the next day are actually “Lower”, that’s a financial disaster.
Also some errors are less important, for example if the real returns were “Lower” or “”Higher” and the model predicted “Neutral”, we missed and opportunity, but didn’t take a very bad decision.
The real profits are when the model predicts “Higher” or “Lower” and those are the returns the next day because we took the right decision before an important move (+/- 3%).<br/><br/>

To visualize the confusion matrix we first import a function from SKLearn and then use it with the two lists of returns and predicted returns.<br/><br/>

<code>
from sklearn.metrics import confusion_matrix<br/>
print confusion_matrix(rets, pred_rets)<br/>
</code>
<br/>
Here's the confusion_matrix:<br/><br/>
<mark>
[[105&nbsp;&nbsp;&nbsp;&nbsp;0&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;22]<br/>
&nbsp;&nbsp;[&nbsp;&nbsp;&nbsp;0&nbsp;&nbsp;&nbsp;81&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;5]<br/>
&nbsp;&nbsp;[&nbsp;&nbsp;20&nbsp;&nbsp;&nbsp;&nbsp;9&nbsp;&nbsp;&nbsp;258]]
</mark>
<br/><br/>


Many people won’t understand that output so I'll borrow a function from SKLearn’s website to make a plot that will help us visualize it better.<br/><br/>
<code>
class_names = "Higher", "Lower", "Neutral"<br/>
def plot_confusion_matrix(cm, classes, normalize=False,<br/>
&nbsp;&nbsp;&nbsp;&nbsp;title='Confusion matrix', cmap=plt.cm.Blues):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;print(cm)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.imshow(cm, interpolation='nearest', cmap=cmap)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.title(title)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.colorbar()<br/>
&nbsp;&nbsp;&nbsp;&nbsp;tick_marks = np.arange(len(classes))<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.xticks(tick_marks, classes, rotation=45)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.yticks(tick_marks, classes)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;fmt = '.2f' if normalize else 'd'<br/>
&nbsp;&nbsp;&nbsp;&nbsp;thresh = cm.max() / 2.<br/>
&nbsp;&nbsp;&nbsp;&nbsp;for i, j in itertools.product(range(cm.shape[0]), range(cm.shape[1])):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;plt.text(j, i, format(cm[i, j], fmt), horizontalalignment="center",<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;color="white" if cm[i, j] > thresh else "black")<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.tight_layout()<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.ylabel('True label')<br/>
&nbsp;&nbsp;&nbsp;&nbsp;plt.xlabel('Predicted label')
</code>
<br/><br/>

Run it using:<br/><br/>
<code>
plt.figure()<br/>
plot_confusion_matrix(confusion_matrix(rets, pred_rets),<br/>
&nbsp;&nbsp;&nbsp;&nbsp;classes=class_names, title='Confusion matrix')
</code>
<br/><br/>

<img src={CM} alt="CM"/>
<br/>

To understand this chart let's go row by row. The first row has the numbers 105, 0 and 22 in that order.
That means the number of "Higher" in the data is 105+0+22=127 and of those 127 the model predicted 105 of them as "Higher", 0 as "Lower" (Great!) and 22 as "Same".
For the second row, the number of "Lower" in the data is 0+81+5=86 and of those 86 the model predicted 0 of them as "Higher" (Great!), 81 as "Lower" and 5 as "Same".
For the last row the number of "Same" in the data is 20+9+258=287 and of those 287 the model predicted 20 of them as "Higher", 9 as "Lower" and 258 (Almost all of them!) as "Same".

<br/><br/>
Now let’s go back to the earlier analysis. The model NEVER forecasts a “Lower” when it was actually “Higher” and viceversa.
We also get a lot of “Higher” and “Lower” on spot, we should be able to make some money using this model. <br/><br/>

Let’s write a short strategy: if the model predicts a higher than +3% movement, we buy, and for a lower than -3% movement, we sell, the rest is just as we had earlier.
Here's the function, called ret_pred for "returns predictor".<br/><br/>

<code>
def ret_pred(x, y):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;btc = [5.0] + [np.nan] * (len(dff)-1)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;cash = [0] + [np.nan] * (len(dff)-1)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;prediction = list(dff['Predictions'])[1:] + [np.nan]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;price = list(dff['Price'])<br/>
&nbsp;&nbsp;&nbsp;&nbsp;for i in range(1, len(dff)-1):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;for j in range(0, 2):<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;if prediction[i] > x/100.0:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;if cash[i] > price[i]:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = btc[i-1] + 1.0<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = cash[i-1] - price[i]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;elif price[i] > cash[i-1] > 0.0:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = btc[i-1] + (cash[i-1] / price[i])<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = 0.0<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;else:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = btc[i - 1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = cash[i - 1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;elif -y/100.0 > prediction[i]:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;if btc[i-1] >= 1.0:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = btc[i-1] - 1.0<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = cash[i-1] + price[i]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;elif 1.0 > btc[i-1] > 0.0:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = 0.0<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = cash[i-1] + (price[i] * btc[i-1])<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;else:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = btc[i - 1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = cash[i - 1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;else:<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;btc[i] = btc[i-1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;cash[i] = cash[i-1]<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['BTC'] = btc<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['Cash'] = cash<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['Strategy'] = dff['Price'] * dff['BTC'] + dff['Cash']<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['BuyNHold'] = 5.0 * dff['Price']<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['StVSBnH_N'] = dff['Strategy'] - dff['BuyNHold']<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['StVSBnH_P'] = ((dff['Strategy'] - dff['BuyNHold']) / dff['BuyNHold']) * 100.0<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['BnH_Vol'] = (dff['BuyNHold'].pct_change() * 100.0).rolling(30).std(ddof=0)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;dff['Strategy_Vol'] = (dff['Strategy'].pct_change() * 100.0).rolling(30).std(ddof=0)<br/>
&nbsp;&nbsp;&nbsp;&nbsp;print "Strategy vs BuyNHold:", (dff['Strategy'][len(dff)-2] - dff['BuyNHold'][len(dff)-2])<br/>
&nbsp;<br/>
</code>
<br/>

I will run the strategy using 3% for buy and 3% for sell because that's the values I used for the confusion matrix.<br/><br/>

<code>
ret_pred(3.0, 3.0)
</code>
<br/><br/>

<mark>
Strategy vs BuyNHold: 90641.3858858
</mark>
<br/><br/>

That's a pretty high number, let's check the chart:<br/><br/>

<code>
plt.xlabel('Date')<br/>
plt.ylabel('US$')<br/>
plt.plot(dff.index, dff['BuyNHold'])<br/>
plt.plot(dff.index, dff['Strategy'])<br/>
plt.legend(shadow=True, fontsize='large')
</code>
<br/><br/>

<img src={RNZ} alt="RNZ"/>
<br/>

And with a little of zoom on the last months:<br/><br/>

<img src={RZ} alt="RZ"/>
<br/>

Now let's see all the charts as we did in Part 4.<br/><br/>

Strategy vs buy and hold in nominal values.<br/><br/>

<code>
plt.xlabel('Date')<br/>
plt.ylabel('US$')<br/>
plt.plot(dff.index, dff['St_VS_BnH_N'], "b")<br/>
plt.legend(shadow=True, fontsize='large')
</code>
<br/><br/>

<img src={RN} alt="Pred Vs BuyNHold N"/>
<br/>

Strategy vs buy and hold in % values.<br/><br/>

<code>
plt.xlabel('Date')<br/>
plt.ylabel('%')<br/>
plt.plot(dff.index, dff['St_VS_BnH_P'], "b")<br/>
plt.legend(shadow=True, fontsize='large')
</code>
<br/><br/>

<img src={RP} alt="Pred Vs BuyNHold P"/>
<br/>

Compare the 30 day volatility of both portfolios.<br/><br/>
<code>
plt.xlabel('Date')<br/>
plt.ylabel('Volatility of Portfolio')<br/>
plt.plot(dff.index, dff['BnH_Vol'], "b")<br/>
plt.plot(dff.index, dff['Strategy_Vol'], "r")<br/>
plt.legend(shadow=True, fontsize='large')
</code>
<br/><br/>

<img src={RV} alt="Pred Vs BuyNHold P"/>
<br/>

The strategy performed better than Buy and Hold the entire time. Regarding volatility, it was lower as in prior cases.
Feel free to play with your own parameters to see different results.<br/><br/>

We still have some problems: we haven't get rid of look-ahead bias and are not including trading costs. Also, the performance is so good that we are probably overfitting.
We will try to solve this in <NavLink to={`${publicPath}finaltests`}>Part 6: Final Tests and Backtesting</NavLink>.

</div>;

class Transformations extends Component {

  componentDidMount() {
    window.onscroll = function() {scrollFunction()};

    function scrollFunction() {
        if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
            document.getElementById("myBtn").style.display = "block";
        } else {
            document.getElementById("myBtn").style.display = "none";
        }
    }

    function findPos(obj) {
      var curtop = 0;
      if (obj.offsetParent) {
          do {
              curtop += obj.offsetTop;
          } while (obj = obj.offsetParent);
      return [curtop];
      }
    }

    document.getElementById("myBtn").onclick = function() {
      const   scrollHeight = window.scrollY,
              scrollStep = Math.PI / ( 1000 / 15 ),
              cosParameter = scrollHeight / 2;
      var     scrollCount = 0,
              scrollMargin,
              scrollInterval = setInterval( function() {
                  if ( window.scrollY > 10 ) {
                      scrollCount = scrollCount + 1;  
                      scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
                      window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
                  } 
                  else clearInterval(scrollInterval); 
              }, 15 );
    }

    const scrollHeight = window.scrollY - findPos(document.getElementById("myNavbar"))[0],
    scrollStep = Math.PI / ( 1000 / 15 ),
    cosParameter = scrollHeight / 2;
    var scrollCount = 0,
    scrollMargin,
    scrollInterval = setInterval( function() {
        if ( window.scrollY - findPos(document.getElementById("myNavbar"))[0] > 10 ) {
            scrollCount = scrollCount + 1;  
            scrollMargin = cosParameter - cosParameter * Math.cos( scrollCount * scrollStep );
            window.scrollTo( 0, ( scrollHeight - scrollMargin ) );
        } 
        else clearInterval(scrollInterval); 
    }, 15 );
  }

  render() {
    return (
        <div id="Data">
        <h1>Part 5: Data Transformation And New Strategy</h1>
          <Text Searchable="Time" description={TransDescription}/>
        </div>
    );
  }
}

export default Transformations;



// WEBPACK FOOTER //
// ./src/components/Transformations.js